import {
  autoUpdate,
  flip,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useTypeahead,
} from '@floating-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useStableCallback } from '../hooks/useStableCallback';
import { type SelectOption, isSelectOptionObject } from './Select.types';

export const SELECT_ALL = Symbol('Select all');

export const useSelectList = <TValue>(
  options: readonly SelectOption[],
  value: TValue | TValue[] | null,
  disabled?: boolean,
) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const { refs, floatingStyles, context, placement } = useFloating<HTMLElement>(
    {
      placement: 'bottom-start',
      // Render in a fixed strategy so the dropdown, portaled to the document
      // body, is not clipped by ancestor overflow/stacking contexts.
      strategy: 'fixed',
      open: menuOpen,
      onOpenChange(isOpen) {
        if (!disabled) {
          setMenuOpen(isOpen);
        }
      },
      whileElementsMounted: autoUpdate,
      middleware: [
        flip({ padding: 10 }),
        // Match the dropdown width to the reference input width, since the
        // floating element no longer inherits it from a relative parent.
        size({
          padding: 10,
          apply({ rects, elements }) {
            elements.floating.style.width = `${rects.reference.width}px`;
          },
        }),
      ],
    },
  );

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const listContentRef = useRef<string[]>([]);
  const isTypingRef = useRef(false);

  const click = useClick(context);
  const dismiss = useDismiss(context);

  // Synchronize selectedIndex with value prop when it changes programmatically
  useEffect(() => {
    if (value === null) {
      setSelectedIndex(null);
      return;
    }
    const newIndex = options.findIndex((option) => {
      const optionValue = isSelectOptionObject(option) ? option.value : option;
      return optionValue === value;
    });
    setSelectedIndex(newIndex !== -1 ? newIndex : null);
  }, [value, options]);

  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    loop: true, // Large list, allow looping
  });

  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    selectedIndex,
    onMatch: menuOpen ? setActiveIndex : setSelectedIndex,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping;
    },
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, listNav, typeahead, click],
  );

  const getOptionLabel = useStableCallback(
    (option: SelectOption | typeof SELECT_ALL) => {
      return option === SELECT_ALL
        ? 'Select all'
        : isSelectOptionObject(option)
          ? option.label
          : option;
    },
  );

  useEffect(() => {
    listContentRef.current = options.map((option) => getOptionLabel(option));
  }, [options]);

  return {
    activeIndex,
    context,
    floatingStyles,
    placement,
    getFloatingProps,
    getItemProps,
    getOptionLabel,
    getReferenceProps,
    isTypingRef,
    listRef,
    menuOpen,
    refs,
    selectedIndex,
    setActiveIndex,
    setMenuOpen,
    setSelectedIndex,
  };
};
